import { useState } from 'react';

import { useSubscription } from 'react-stomp-hooks';
import { emitWidgetReady } from 'services/eventBus/narrativeWidgetsEventBus';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationSetComponentSpecificFields } from 'api/mutations/setComponentSpecificFields.mutation';

import {
    NARRATIVE_GENERATED_TYPE,
    ParsedMessage,
} from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/hooks/useNarrative';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AutomatedNarrativeParams } from 'components/Dashboards/Reporting/threeDotsMenu/hooks/useAddAutomatedNarrativeWidgetToReport';

export type UseAutomatedNarrativeProvider = ReturnType<typeof useAutomatedNarrativeProvider>;

type AutomatedNarrativeLocalFilters = IComponentProps['localFilters'] & AutomatedNarrativeParams;

interface Props {
    localFilters: AutomatedNarrativeLocalFilters;
    widgetId: IComponentProps['widgetId'];
}

export const useAutomatedNarrativeProvider = ({ localFilters, widgetId }: Props) => {
    const { email } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();

    const originParentTitle = localFilters?.parentTitle;
    const originParentWidgetId = localFilters?.parentWidgetId;
    const originParentParentComponentKey = localFilters?.parentComponentKey;
    const [narrative, setNarrative] = useState<string | undefined>(undefined);

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body) as ParsedMessage;
        if (type === NARRATIVE_GENERATED_TYPE) {
            const { key: widgetIdApiResponse, value: generatedNarrativeApiResponse } = message;
            const matchesCurrentWidget = Number(widgetIdApiResponse) === Number(originParentWidgetId);
            if (matchesCurrentWidget) {
                setNarrative(generatedNarrativeApiResponse);
                //Notify that the widget is ready
                //used for tracking loading state in reports for enable export when all narrative widgets are ready
                if (widgetId !== undefined) {
                    emitWidgetReady(widgetId);
                }
            }
        }
    });

    const { mutateAsync: setWidgetSpecificFields } = useMutationSetComponentSpecificFields();
    const onToggleOffAutomatedNarrativeForOriginParentWidget = async () => {
        await setWidgetSpecificFields(
            {
                componentId: originParentWidgetId,
                fieldsToUpdate: {
                    showNarrative: false,
                },
            },
            {
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    return {
        ui: {
            state: {
                parentTitle: originParentTitle,
                parentWidgetId: originParentWidgetId,
            },
            vars: {
                narrative,
                originParentParentComponentKey,
            },
            setters: {
                setNarrative,
            },
        },
        api: {
            onToggleOffAutomatedNarrativeForOriginParentWidget: onToggleOffAutomatedNarrativeForOriginParentWidget,
        },
    };
};
